import { Injectable, NgZone } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  fromEvent,
  map,
  merge,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserInactivityService {
  private isInactive$ = new BehaviorSubject<boolean>(false); // load with page in active mode
  private inactiveSub = new Subscription(); // to save the service subscrition object
  public inactive$ = this.isInactive$.asObservable(); // User activity observable
  private userStatus$ = new BehaviorSubject<string>('Available');
  public status$ = this.userStatus$.asObservable();
  private timeout: number = /* 2000 */ 300000; // timeout set to 5 min 300 000ms
  public statusLock: string = '';
  public selectedStatus = '_available'; // Default status0
    // Status variables 
    statuses = ['_available', '_away', '_busy', '_offline'];
 statusColors: Record<string, string> = {
    '_available': 'green',
    '_away': 'orange',
    '_busy': 'red',
    '_offline': 'grey'
  };


  constructor(private NgZone: NgZone) {
    //Initialize the listener
    this.initListeners();
  }
  ngOnDestroy(): void {
    //Unsubscribe to the user inactive observable
    this.inactiveSub.unsubscribe();
  }

  /**
   * Function that create a single observable combining
   * all user activity event listener to watch for user
   * Activity on the page
   */
  initListeners() {
    //Use NgZone service to watch for the following event
    //outside of the change detection cycle to avoid performance drop
    //caused by the change detector system
    this.NgZone.runOutsideAngular(() => {
      //Create flags to watch for user activity

      //Flag that checks for document/page/tab visibility is in the background or in a minimized window,or is otherwise not visible to the user.
      const visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
        map(() => document.visibilityState === 'hidden')
      );

      // Flag that combine window blur and focus events to track user attention:
      // - blur → user left the window (inactive)
      // - focus → user returned to the window (active)
      const blurFocus$ = merge(
        fromEvent(document, 'blur').pipe(map(() => true)),
        fromEvent(document, 'focus').pipe(map(() => false))
      );

      // Flag that combine user interaction events to track user activity on page
      const userActivityEvents$ = merge(
        fromEvent(document, 'mousemove'), // mouse movement
        fromEvent(document, 'keydown'), // keyboard input
        fromEvent(document, 'mousedown'), // mouse click or press
        fromEvent(document, 'touchstart') // touchscreen tap for mobile
      );

      // Detect user inactivity by monitoring gaps between activity events.
      // If no interaction events occurs within the timeout,
      // emit `true` to indicate the user is inactive.
      const userInactive$ = userActivityEvents$.pipe(
        debounceTime(this.timeout), // Wait for timeout (5min) of no activity
        map(() => true), // User is considered inactive
        startWith(false) // Initially assume user is active
      );

      // Flag that emit false immediately when user becomes active again
      const userActive$ = userActivityEvents$.pipe(map(() => false));

      // Combine all flags into one
      const combined$ = merge(
        visibilityChange$,
        blurFocus$,
        userInactive$,
        userActive$
      ).pipe(
        distinctUntilChanged() // Prevent duplicate emissions
      );

      // Saves the subscription
      this.inactiveSub.add(
        combined$.subscribe((inactive: boolean) => {
          this.NgZone.run(() => {
            this.isInactive$.next(inactive);
          });
        })
      );
    });
  }

  /**
   * Function used to subscribe to the isInactive observablle
   * @returns
   */
  getUserInactiveStatus() {
    return this.inactive$;
  }

  /**
   * Function used to acces the isInactive value
   * @returns
   * - True: if user is inactive in current page/tab
   * - False: if user actice in current page/tab
   */
  isUserInactive(): boolean {
    return this.isInactive$.getValue();
  } 
  // Force user to be inactive manually
setUserInactive(): void {
  this.isInactive$.next(true);
}
setUserActive(): void {
  this.isInactive$.next(false);
}
 /**
   * Function used to set user status to Available
   * 
   */
  setUserAvailable(): void {
     this.userStatus$.next('Available');
     this.statusLock = '_available'
  }
 /**
   * Function used to set user status to Away
   */
  setUserAway(): void {
     this.userStatus$.next('Away');
     this.statusLock = '_away'
  }
 /**
   * Function used to set user status to Busy
   */
  setUserBusy(): void {
     this.userStatus$.next('Busy');
     this.statusLock = '_busy'
  }
 /**
   * Function used to set user status to offline
   */
  setUserOffline(): void {
     this.userStatus$.next('Offline');
     this.statusLock = '_offline'
  }
 /**
   * Function used to subscribe to the status$ observablle
   */
  getUserSelectedStatus(): Observable<string> {
    return this.status$;
  }

}
